import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { TimePicker } from "@/components/ui/time-picker";
import { panelDateLocale } from "@/domains/pm/utils/panelDates";
import {
  PROTOCOL_COLUMNS,
  PROTOCOL_SECTIONS,
  type ProtocolColumnId,
  type ProtocolColumnSelection,
  type ProtocolDocumentDate,
  type ProtocolNote,
  type ProtocolSectionId,
  type ProtocolSectionSelection,
} from "@/domains/pm/utils/protocol";
import { ProtocolNotesField } from "./ProtocolNotesField";
import { SectionHeading } from "./SectionHeading";
import type { ProtocolCommentsStatus } from "./useProtocolComments";
import type { Language, Translations } from "@/lib/i18n/types";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

interface SectionToggleProps {
  id: string;
  name: string;
  help: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function SectionToggle({ id, name, help, checked, onChange }: SectionToggleProps) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id={id}
        checked={checked}
        className="mt-0.5"
        onCheckedChange={(value) => onChange(value === true)}
      />
      <div className="min-w-0 flex-1">
        <Label htmlFor={id} className="font-medium">
          {name}
        </Label>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{help}</p>
      </div>
    </div>
  );
}

interface ProtocolContentStepProps {
  sections: ProtocolSectionSelection;
  columns: ProtocolColumnSelection;
  commentsStatus: ProtocolCommentsStatus;
  onColumnToggle: (id: ProtocolColumnId, checked: boolean) => void;
  onRetryComments: () => void;
  notes: ProtocolNote[];
  documentDate: ProtocolDocumentDate;
  onDocumentDateChange: (value: ProtocolDocumentDate) => void;
  /** The interface's language, for the pickers — not the PDF's. */
  language: Language;
  copy: ProtocolCopy;
  onSectionToggle: (id: ProtocolSectionId, checked: boolean) => void;
  onNotesChange: (notes: ProtocolNote[]) => void;
  /** The attachments band, printed last — so it sits last here too. */
  attachments: React.ReactNode;
  /** The issues' own files: a choice about the table, so it sits under it. */
  issueAttachmentsOption: React.ReactNode;
}

/**
 * What the protocol prints: the built-in sections, and the ones the user writes.
 *
 * `PROTOCOL_SECTIONS` drives the list and `copy.sections[id]` is looked up by
 * id, so a new section is a compile error until its name and explanation exist
 * — the same contract the /documents template forms use.
 */
export function ProtocolContentStep(props: ProtocolContentStepProps) {
  const { copy } = props;
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold">{copy.contentStep}</h3>
        <p className="mt-1 text-xs text-muted-foreground">{copy.contentStepHint}</p>
      </div>

      <DocumentDateField
        value={props.documentDate}
        language={props.language}
        copy={copy.documentDate}
        onChange={props.onDocumentDateChange}
      />

      {/* No rules between the rows and no band border: a tick box, a name and
          a line of explanation already group on their own, and a divider per
          row turned four choices into a table. Space separates instead. */}
      <section className="space-y-3">
        {PROTOCOL_SECTIONS.map((id) => (
          <div key={id} className="space-y-3">
            <SectionToggle
              id={`protocol-section-${id}`}
              name={copy.sections[id].name}
              help={copy.sections[id].help}
              checked={props.sections[id]}
              onChange={(checked) => props.onSectionToggle(id, checked)}
            />
            {/* The columns are meaningless without the table, so they appear
                with it rather than sitting greyed out under an unticked
                parent. */}
            {id === "issues" && props.sections.issues && (
              <div className="ml-7 space-y-2 border-l pl-4">
                <p className="text-xs text-muted-foreground">{copy.columnsHint}</p>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {PROTOCOL_COLUMNS.map((column) => (
                    <label key={column} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        checked={props.columns[column]}
                        onCheckedChange={(value) => props.onColumnToggle(column, value === true)}
                      />
                      {copy.columns[column]}
                    </label>
                  ))}
                </div>
                {/* Only comments carries an explanation: it is the one choice
                    that does more than its label says, and the one that costs
                    a fetch. */}
                {props.columns.commentCount && (
                  <p className="text-xs text-muted-foreground">{copy.columnsCommentsHelp}</p>
                )}
                {props.commentsStatus === "loading" && (
                  <p className="text-xs text-muted-foreground">{copy.commentsLoading}</p>
                )}
                {props.commentsStatus === "error" && (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-destructive">
                    {copy.commentsError}
                    <Button variant="outline" size="sm" onClick={props.onRetryComments}>
                      {copy.retry}
                    </Button>
                  </div>
                )}
                {props.issueAttachmentsOption}
              </div>
            )}
          </div>
        ))}
      </section>

      <section>
        <SectionHeading>{copy.notesHeading}</SectionHeading>
        <p className="mb-3 mt-1 text-xs text-muted-foreground">{copy.notesHint}</p>
        <ProtocolNotesField notes={props.notes} copy={copy} onChange={props.onNotesChange} />
      </section>

      {props.attachments}
    </div>
  );
}

/**
 * The date printed in the protocol's top-right corner. A time needs a date to
 * mean anything, so the time picker waits for one — and clearing the date
 * leaves the time where it was, ready if the date comes back.
 */
function DocumentDateField({
  value,
  language,
  copy,
  onChange,
}: {
  value: ProtocolDocumentDate;
  language: Language;
  copy: ProtocolCopy["documentDate"];
  onChange: (value: ProtocolDocumentDate) => void;
}) {
  // One row: the label beside its pickers, the explanation on hover and for
  // screen readers — the field is prefilled and rarely touched, so it should
  // not take the step's first screenful.
  return (
    <section className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <Label htmlFor="protocol-document-date" className="font-medium" title={copy.hint}>
        {copy.label}
      </Label>
      <p className="sr-only">{copy.hint}</p>
      <div className="flex flex-wrap items-center gap-2">
        <DatePicker
          id="protocol-document-date"
          value={value.date}
          onChange={(date) => onChange({ ...value, date })}
          placeholder={copy.placeholder}
          clearLabel={copy.clear}
          locale={panelDateLocale(language)}
          displayFormat={language === "et" ? "dd.MM.y" : "dd/MM/y"}
          className="h-9 w-40"
        />
        <Label htmlFor="protocol-document-time" className="sr-only">
          {copy.time}
        </Label>
        <TimePicker
          id="protocol-document-time"
          value={value.time}
          onChange={(time) => onChange({ ...value, time })}
          disabled={!value.date}
          hoursLabel={copy.hours}
          minutesLabel={copy.minutes}
          clearLabel={copy.clear}
          className="h-9 w-24"
        />
      </div>
    </section>
  );
}
